/**
 * Spatially cut freeform wall masses with door/window openings from room walls
 * that planar-overlap them — similar to foundationOpeningCuts, but producing WallCuts
 * for solidWallWithOpenings.
 *
 * Freeform masses are not in the room planar-graph half-edge maps, so room openings
 * never attach via halfEdgeIndex. WINDOW_V2/DOOR_V2 carry world (x, y); deprecated
 * WINDOW/DOOR (including automatic room-type openings) are resolved from half-edge + param.
 */
import { getDoorOpeningHeight } from "building-generation/elements/doors/doorUtils";
import { wallCutNew } from "building-generation/elements/wallCuts";
import { getWindowHeight, windowHeadHeight } from "building-generation/elements/window/windowHeightTools";
import type { GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import { WindowPanelType } from "building-types/settings/windowTypesEnum";
import type { WallCut } from "building-types/walls/wallCutTypes";
import { WallType } from "building-types/walls/wallEnums";
import type { WallMassSystem } from "building-types/walls/wallSytemTypes";
import { FREEFORM_WALL_HALF_EDGE_INDEX } from "building-types/walls/wallTypes";
import type { SolidWallMass } from "building-types/walls/wallTypes";
import type { GeneratedWindowPlacement } from "building-types/windows/windowTypes";
import { closestParamLine2 } from "geom/closestPoint";
import { DIST_EPSILON } from "geom/core";
import { line2Eval, line2Len } from "geom/line2";
import { planarGraphHalfEdgeEnd, planarGraphHalfEdgeStart } from "geom/planarGraph";
import { polyLine2EdgeTangent } from "geom/polyLine2";
import type { LineAnchor } from "geom/polyLine2Types";
import { ray2DotProject } from "geom/ray2";
import { vec2Dist } from "geom/vec2";

export type FreeformWallOpeningCutsByMassId = { [wallMassId: string]: WallCut[] };

interface CollectFreeformWallOpeningCutsParams {
  readonly windows: readonly GeneratedWindowPlacement[];
  readonly doors: readonly GeneratedDoorPlacement[];
  readonly wallMassSystem: WallMassSystem;
}

interface OpeningCutCandidate {
  readonly centerX: number;
  readonly centerY: number;
  readonly width: number;
  readonly height: number;
  readonly bottom: number;
}

/**
 * Returns wall cuts keyed by freeform solid mass id for openings whose world
 * center lies on (or very near) that mass's path and overlaps its span.
 */
export function collectFreeformWallOpeningCuts({
  windows,
  doors,
  wallMassSystem,
}: CollectFreeformWallOpeningCutsParams): FreeformWallOpeningCutsByMassId {
  // FREEFORM_WALL_HALF_EDGE_INDEX hazard: only collect cuts for freeform solid masses;
  // room-owned masses resolve openings via halfEdgeToWindowSet/DoorSet instead.
  const freeformMasses = Object.values(wallMassSystem.walls).filter(
    (wall): wall is SolidWallMass =>
      wall?.wallType === WallType.SOLID_MASS && wall.path.halfEdgeIndex === FREEFORM_WALL_HALF_EDGE_INDEX,
  );

  if (freeformMasses.length === 0) {
    return {};
  }

  const cutsByMassId: FreeformWallOpeningCutsByMassId = {};

  function addCut(massId: string, cut: WallCut) {
    const existing = cutsByMassId[massId];
    if (existing) {
      existing.push(cut);
    } else {
      cutsByMassId[massId] = [cut];
    }
  }

  function applyCandidate(candidate: OpeningCutCandidate) {
    const halfWidth = candidate.width / 2;
    for (const mass of freeformMasses) {
      const edgeParam = freeformMassOpeningEdgeParam(mass, candidate.centerX, candidate.centerY, halfWidth);
      if (edgeParam === undefined) {
        continue;
      }
      addCut(
        mass.id,
        wallCutNew({
          edgeParam,
          edgeParamNormalized: false,
          edgeParamAbsolute: true,
          width: candidate.width,
          height: candidate.height,
          bottom: candidate.bottom,
          opening: undefined,
        }),
      );
    }
  }

  for (const window of windows) {
    if (window.materials?.panelType === WindowPanelType.FAUX_SHUTTER) {
      continue;
    }
    const candidate = windowOpeningCutCandidate(window, wallMassSystem);
    if (candidate) {
      applyCandidate(candidate);
    }
  }

  for (const door of doors) {
    const candidate = doorOpeningCutCandidate(door, wallMassSystem);
    if (candidate) {
      applyCandidate(candidate);
    }
  }

  return cutsByMassId;
}

function windowOpeningCutCandidate(
  window: GeneratedWindowPlacement,
  wallMassSystem: WallMassSystem,
): OpeningCutCandidate | undefined {
  const width = window.descriptor.width;
  const height = getWindowHeight(window.descriptor);
  const top = windowHeadHeight(window);
  const bottom = top - height;

  if (window.type === "WINDOW_V2") {
    const { x, y } = window;
    if (x === undefined || y === undefined) {
      return undefined;
    }
    return { centerX: x, centerY: y, width, height, bottom };
  }

  const center = openingCenterFromHalfEdgeParam({
    halfEdgeIndex: window.halfEdgeIndex,
    param: window.param,
    paramNormalized: window.paramNormalized,
    paramAnchor: window.paramAnchor,
    wallMassSystem,
  });
  if (!center) {
    return undefined;
  }
  return { centerX: center[0], centerY: center[1], width, height, bottom };
}

function doorOpeningCutCandidate(
  door: GeneratedDoorPlacement,
  wallMassSystem: WallMassSystem,
): OpeningCutCandidate | undefined {
  const width = door.descriptor.width;
  const height = getDoorOpeningHeight(door.descriptor);
  const { verticalOffset = 0 } = door;
  const wallBaseOffset = massBaseOffset(wallMassSystem, door.halfEdgeIndex);
  const top = height - wallBaseOffset + verticalOffset;
  const bottom = top - height;

  if (door.type === "DOOR_V2") {
    const { x, y } = door;
    if (x === undefined || y === undefined) {
      return undefined;
    }
    return { centerX: x, centerY: y, width, height, bottom };
  }

  const center = openingCenterFromHalfEdgeParam({
    halfEdgeIndex: door.halfEdgeIndex,
    param: door.param,
    paramNormalized: door.paramNormalized,
    paramAnchor: door.paramAnchor,
    wallMassSystem,
  });
  if (!center) {
    return undefined;
  }
  return { centerX: center[0], centerY: center[1], width, height, bottom };
}

/**
 * Resolve deprecated WINDOW/DOOR placements (automatic room-type openings) to world XY
 * by evaluating param along the host planar-graph half-edge.
 */
function openingCenterFromHalfEdgeParam({
  halfEdgeIndex,
  param,
  paramNormalized,
  paramAnchor,
  wallMassSystem,
}: {
  readonly halfEdgeIndex: number;
  readonly param: number;
  readonly paramNormalized: boolean;
  readonly paramAnchor: LineAnchor;
  readonly wallMassSystem: WallMassSystem;
}): [number, number] | undefined {
  const planarGraph = wallMassSystem.wallIndex.planarGraph.planarGraph;
  if (halfEdgeIndex < 0 || halfEdgeIndex >= planarGraph.halfEdges.length / 2) {
    return undefined;
  }

  const [startX, startY] = planarGraphHalfEdgeStart(planarGraph, halfEdgeIndex);
  const [endX, endY] = planarGraphHalfEdgeEnd(planarGraph, halfEdgeIndex);
  const edgeLength = line2Len(startX, startY, endX, endY);
  if (edgeLength <= DIST_EPSILON) {
    return undefined;
  }

  const t = paramFromLineAnchor(param, paramNormalized, paramAnchor, edgeLength);
  const [x, y] = line2Eval(startX, startY, endX, endY, t, paramNormalized);
  return [x, y];
}

function paramFromLineAnchor(
  param: number,
  paramNormalized: boolean,
  paramAnchor: LineAnchor,
  edgeLength: number,
): number {
  switch (paramAnchor) {
    case "END": {
      return paramNormalized ? 1 - param : edgeLength - param;
    }
    case "CENTER": {
      return paramNormalized ? 0.5 + param : edgeLength / 2 + param;
    }
    case "START":
    default: {
      return param;
    }
  }
}

function massBaseOffset(wallMassSystem: WallMassSystem, halfEdgeIndex: number): number {
  const wallId = wallMassSystem.halfEdgeToWall[halfEdgeIndex];
  const wall = wallId ? wallMassSystem.walls[wallId] : undefined;
  if (!wall || wall.wallType !== WallType.SOLID_MASS) {
    return 0;
  }
  return wall.path.baseOffset;
}

/**
 * If the opening center is coplanar with the freeform path and its width overlaps
 * the path span, returns the absolute edgeParam on mass.leftEdge for wallCutNew.
 */
export function freeformMassOpeningEdgeParam(
  mass: SolidWallMass,
  centerX: number,
  centerY: number,
  halfWidth: number,
): number | undefined {
  const { startX, startY, endX, endY } = mass.path;
  const pathLen = vec2Dist(startX, startY, endX, endY);
  if (pathLen <= DIST_EPSILON) {
    return undefined;
  }

  const t = closestParamLine2(startX, startY, endX, endY, centerX, centerY, false);
  const [px, py] = line2Eval(startX, startY, endX, endY, t, false);
  const distToPath = vec2Dist(px, py, centerX, centerY);
  // Allow a small lateral tolerance so an opening centered on the room wall's face
  // (offset by up to half the freeform wall's thickness) still matches a coplanar
  // freeform centerline. Using the full thickness here would also match openings
  // on a *different*, closely-spaced coplanar wall.
  const lateralTolerance = Math.max(mass.thickness / 2, 1);
  if (distToPath > lateralTolerance) {
    return undefined;
  }

  if (t + halfWidth < -DIST_EPSILON || t - halfWidth > pathLen + DIST_EPSILON) {
    return undefined;
  }

  const frontEdge = mass.leftEdge;
  const [dirX, dirY] = polyLine2EdgeTangent(frontEdge, 0);
  return ray2DotProject(frontEdge[0], frontEdge[1], dirX, dirY, centerX, centerY);
}
